import type { ComponentProps, ReactNode } from 'react';
import * as Dropdown from '@radix-ui/react-dropdown-menu';

import { cn, POPOVER_MOTION } from '../lib/cn';
import { GROUP_LABEL } from './group-label';

export const Menu = Dropdown.Root;
export const MenuTrigger = Dropdown.Trigger;

export const MenuContent = ({
  className,
  children,
  ...rest
}: ComponentProps<typeof Dropdown.Content>) => (
  <Dropdown.Portal>
    <Dropdown.Content
      sideOffset={6}
      alignOffset={8}
      collisionPadding={8}
      className={cn(
        'w-menu rounded-panel border border-border-raised bg-surface-raised p-label shadow-panel',
        'origin-(--radix-dropdown-menu-content-transform-origin)',
        POPOVER_MOTION,
        className,
      )}
      {...rest}
    >
      {children}
    </Dropdown.Content>
  </Dropdown.Portal>
);

export const MenuItem = ({
  className,
  selected,
  description,
  tone = 'default',
  children,
  ...rest
}: ComponentProps<typeof Dropdown.Item> & {
  selected?: boolean;
  description?: ReactNode;
  tone?: 'default' | 'danger';
}) => (
  <Dropdown.Item
    className={cn(
      // `min-h`, never a fixed height: a label that wraps to two lines overflows its box and prints over the item below.
      'flex min-h-menu-item cursor-default items-center gap-inset rounded-item px-inset py-label text-body',
      // One icon size in every menu row; a key glyph inside a Keycap keeps its own.
      '[&_svg]:size-icon-menu [&_kbd_svg]:size-icon-sm',
      'data-[highlighted]:bg-hover-raised data-[highlighted]:outline-none',
      selected ? 'text-primary' : 'text-muted',
      className,
    )}
    {...rest}
  >
    {description === undefined ? (
      children
    ) : (
      <span className="flex min-w-0 flex-col">
        <span
          className={cn(
            'text-body',
            rest.disabled ? 'text-faint' : tone === 'danger' ? 'text-danger-text' : 'text-primary',
          )}
        >
          {children}
        </span>
        <span className="text-meta leading-17 text-muted">{description}</span>
      </span>
    )}
  </Dropdown.Item>
);

export const MenuLabel = ({ children }: { children: ReactNode }) => (
  <Dropdown.Label className={cn('px-inset pt-2 pb-1', GROUP_LABEL)}>{children}</Dropdown.Label>
);

export const MenuSeparator = () => (
  <Dropdown.Separator className="my-label h-px bg-border-raised" />
);
